import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import {
  parseIdCardConfigFromNotes,
  listIdCardConfigLineItems,
} from 'Containers/Billing/idCardServiceConfig';
import {
  parseBiometricConfigFromNotes,
  BIOMETRIC_KINDS,
} from 'Containers/Billing/biometricServiceConfig';

function CompanyBillingSummaryModal({
  open,
  loading,
  data,
  onClose,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState('all'); // 'all' | 'tax' | 'issued'

  // Reset state on open
  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setInvoiceTypeFilter('all');
    }
  }, [open]);

  const detailData = data;

  const toNum = (v) => {
    const n = parseFloat(v || 0);
    return Number.isFinite(n) ? n : 0;
  };

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Aggregate totals across pricing services
  const orderTotals = useMemo(() => {
    return (detailData?.pricing_services || []).reduce(
      (acc, svc) => {
        const itemCount = toNum(svc.item_count || 1);
        const amount = toNum(svc.service_amount);
        const gstPercent = toNum(svc.service_gst_percentage);
        const orderTotal =
          svc.order_total_amount !== undefined && svc.order_total_amount !== null
            ? toNum(svc.order_total_amount)
            : amount * itemCount + (amount * itemCount * gstPercent) / 100;
        acc.serviceAmount += amount;
        acc.count += itemCount;
        acc.orderTotal += orderTotal;
        acc.invoicedTotal += toNum(svc.invoice_amount || svc.issue_invoice_amount);
        acc.discountTotal += toNum(svc.invoice_discount_amount);
        return acc;
      },
      { serviceAmount: 0, count: 0, orderTotal: 0, invoicedTotal: 0, discountTotal: 0 }
    );
  }, [detailData]);

  // Flattened invoices (Tax Invoices)
  const taxInvoiceItems = useMemo(() => {
    if (!Array.isArray(detailData?.pricing_services)) return [];
    return detailData.pricing_services.flatMap((s) =>
      (Array.isArray(s.invoice_items) ? s.invoice_items : []).map((it) => ({
        ...it,
        service_name: s.service_name || 'Service',
        type: 'Tax Invoice',
      }))
    );
  }, [detailData]);

  // Flattened issued invoices (Proforma / Issued Invoices)
  const issueInvoiceItems = useMemo(() => {
    if (!Array.isArray(detailData?.pricing_services)) return [];
    return detailData.pricing_services.flatMap((s) =>
      (Array.isArray(s.issue_invoice_items) ? s.issue_invoice_items : []).map((it) => ({
        ...it,
        service_name: s.service_name || 'Service',
        type: 'Issued Invoice',
      }))
    );
  }, [detailData]);

  // Combined invoice items
  const allInvoiceItems = useMemo(() => {
    return [...taxInvoiceItems, ...issueInvoiceItems];
  }, [taxInvoiceItems, issueInvoiceItems]);

  const filteredInvoices = useMemo(() => {
    if (invoiceTypeFilter === 'tax') return taxInvoiceItems;
    if (invoiceTypeFilter === 'issued') return issueInvoiceItems;
    return allInvoiceItems;
  }, [invoiceTypeFilter, taxInvoiceItems, issueInvoiceItems, allInvoiceItems]);

  const paymentsList = detailData?.payments || [];
  const invoicesHeaderList = detailData?.invoices || [];

  // Payment terms across services
  const paymentTermsList = useMemo(() => {
    if (!Array.isArray(detailData?.pricing_services)) return [];
    return detailData.pricing_services.flatMap((s) =>
      (Array.isArray(s.payment_terms) ? s.payment_terms : []).map((term) => ({
        ...term,
        service_name: s.service_name || 'Service',
      }))
    );
  }, [detailData]);

  const totalPaid = toNum(detailData?.payments_summary?.total_paid_amount);
  const pendingBalance = Math.max(0, orderTotals.orderTotal - totalPaid);
  const collectionPercent =
    orderTotals.orderTotal > 0
      ? Math.min(100, Math.round((totalPaid / orderTotals.orderTotal) * 100))
      : 0;

  const getStatusColor = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('paid') && !s.includes('unpaid') && !s.includes('partial')) {
      return { bg: '#dcfce7', text: '#15803d', label: 'Paid' };
    }
    if (s.includes('part')) {
      return { bg: '#ffedd5', text: '#c2410c', label: 'Partial' };
    }
    if (s.includes('unpaid') || s.includes('draft')) {
      return { bg: '#fee2e2', text: '#b91c1c', label: status || 'Unpaid' };
    }
    return { bg: '#f1f5f9', text: '#475569', label: status || 'Active' };
  };

  // Helper to parse internal config JSON into readable specifications instead of dumping raw JSON
  const renderFormattedNotes = (notes, title = 'Itemized Specifications') => {
    if (!notes || typeof notes !== 'string') return null;
    const trimmed = notes.trim();
    if (!trimmed) return null;

    // 1. Check ID Card config
    if (trimmed.startsWith('__ID_CARD_CONFIG__')) {
      const config = parseIdCardConfigFromNotes(trimmed);
      const lineItems = config ? listIdCardConfigLineItems(config) : [];
      if (lineItems.length > 0) {
        return (
          <Box mt={1} p={1} bgcolor="#f8fafc" borderRadius={1.5} border="1px dashed #cbd5e1">
            <Typography variant="caption" fontWeight={700} color="#475569" display="block" mb={0.5}>
              📋 {title} ({lineItems.length}):
            </Typography>
            <Grid container spacing={0.8}>
              {lineItems.map((item, idx) => (
                <Grid item xs={12} sm={6} key={`id-item-${idx}`}>
                  <Box
                    p={0.6}
                    px={1}
                    bgcolor="#ffffff"
                    borderRadius={1}
                    border="1px solid #e2e8f0"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="#1e293b" fontWeight={600} noWrap sx={{ maxWidth: '65%' }}>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" color="#64748b" fontWeight={700}>
                      {item.count} pcs @ ₹{item.unitPrice}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      }
      return null;
    }

    // 2. Check Biometric config
    if (trimmed.startsWith('__BIOMETRIC_CONFIG__')) {
      const config = parseBiometricConfigFromNotes(trimmed);
      if (config) {
        const kindLabel = BIOMETRIC_KINDS[config.kind]?.name || 'Biometric';
        return (
          <Box mt={1} p={1} bgcolor="#f8fafc" borderRadius={1.5} border="1px dashed #cbd5e1">
            <Typography variant="caption" fontWeight={700} color="#475569" display="block">
              🔒 {kindLabel}: {config.deviceCount ? `${config.deviceCount} Devices` : ''}{' '}
              {config.fee ? `• Rate: ${formatAmount(config.fee)}` : ''}{' '}
              {config.renewalFee ? `• Renewal: ${formatAmount(config.renewalFee)}` : ''}
            </Typography>
          </Box>
        );
      }
      return null;
    }

    // 3. If raw JSON string starts with '{' or '__', hide internal representation
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || trimmed.startsWith('__')) {
      return null;
    }

    // 4. Regular human note
    return (
      <Typography variant="caption" color="text.secondary" fontStyle="italic" display="block" mt={0.5}>
        Note: {trimmed}
      </Typography>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100%' : '90vh',
          backgroundColor: '#f8fafc',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      {/* DIALOG HEADER */}
      <DialogTitle
        sx={{
          p: { xs: 1.5, sm: 2 },
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={800} color="#0f172a" noWrap>
              {detailData?.company_name || 'Company Billing Summary'}
            </Typography>
            {detailData?.student_strength ? (
              <Chip
                icon={<SchoolOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                label={`${detailData.student_strength} Students`}
                size="small"
                sx={{
                  bgcolor: '#f1f5f9',
                  color: '#334155',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: 22,
                }}
              />
            ) : null}
            {detailData?.year_name ? (
              <Chip
                label={detailData.year_name}
                size="small"
                sx={{
                  bgcolor: '#eff6ff',
                  color: '#2563eb',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: 22,
                }}
              />
            ) : null}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.3}>
            Simplified financial breakdown, orders, invoices & payment history
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.8}>
          {totalCount > 1 && (
            <Box
              display="flex"
              alignItems="center"
              bgcolor="#f1f5f9"
              borderRadius={2}
              px={0.8}
              py={0.3}
            >
              <IconButton
                size="small"
                disabled={!onPrev || currentIndex <= 0}
                onClick={onPrev}
                title="Previous Company"
                sx={{ p: 0.3, color: '#334155' }}
              >
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                fontWeight={700}
                color="#334155"
                sx={{ px: 0.6, fontSize: '0.72rem', whiteSpace: 'nowrap' }}
              >
                {currentIndex + 1} of {totalCount}
              </Typography>
              <IconButton
                size="small"
                disabled={!onNext || currentIndex >= totalCount - 1}
                onClick={onNext}
                title="Next Company"
                sx={{ p: 0.3, color: '#334155' }}
              >
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <IconButton size="small" onClick={onClose} sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* EXECUTIVE FINANCIAL SUMMARY BANNER (KPIs) */}
      {detailData && (
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <Grid container spacing={1}>
            {/* Order Total */}
            <Grid item xs={6} sm={3}>
              <Box p={1} bgcolor="#f8fafc" borderRadius={2} border="1px solid #e2e8f0">
                <Typography variant="caption" color="#64748b" display="block" fontSize="0.68rem" fontWeight={700}>
                  ORDER TOTAL
                </Typography>
                <Typography variant="body1" fontWeight={800} color="#1e293b" noWrap>
                  {formatAmount(orderTotals.orderTotal)}
                </Typography>
              </Box>
            </Grid>

            {/* Total Invoiced */}
            <Grid item xs={6} sm={3}>
              <Box p={1} bgcolor="#f8fafc" borderRadius={2} border="1px solid #e2e8f0">
                <Typography variant="caption" color="#64748b" display="block" fontSize="0.68rem" fontWeight={700}>
                  TOTAL INVOICED
                </Typography>
                <Typography variant="body1" fontWeight={800} color="#334155" noWrap>
                  {formatAmount(orderTotals.invoicedTotal)}
                </Typography>
              </Box>
            </Grid>

            {/* Total Paid */}
            <Grid item xs={6} sm={3}>
              <Box p={1} bgcolor="#f0fdf4" borderRadius={2} border="1px solid #bbf7d0">
                <Typography variant="caption" color="#16a34a" display="block" fontSize="0.68rem" fontWeight={700}>
                  TOTAL PAID
                </Typography>
                <Typography variant="body1" fontWeight={800} color="#15803d" noWrap>
                  {formatAmount(totalPaid)}
                </Typography>
              </Box>
            </Grid>

            {/* Balance Due */}
            <Grid item xs={6} sm={3}>
              <Box
                p={1}
                bgcolor={pendingBalance > 0 ? '#fff7ed' : '#f0fdf4'}
                borderRadius={2}
                border={`1px solid ${pendingBalance > 0 ? '#fed7aa' : '#bbf7d0'}`}
              >
                <Typography
                  variant="caption"
                  color={pendingBalance > 0 ? '#ea580c' : '#16a34a'}
                  display="block"
                  fontSize="0.68rem"
                  fontWeight={700}
                >
                  BALANCE DUE
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={800}
                  color={pendingBalance > 0 ? '#c2410c' : '#15803d'}
                  noWrap
                >
                  {formatAmount(pendingBalance)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Collection Progress Bar */}
          {orderTotals.orderTotal > 0 && (
            <Box mt={1.2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.3}>
                <Typography variant="caption" color="#64748b" fontSize="0.7rem" fontWeight={600}>
                  Collection Progress: <strong>{collectionPercent}%</strong>
                </Typography>
                <Typography variant="caption" color="#64748b" fontSize="0.7rem">
                  {formatAmount(totalPaid)} of {formatAmount(orderTotals.orderTotal)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={collectionPercent}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    backgroundColor: collectionPercent >= 100 ? '#16a34a' : collectionPercent > 50 ? '#3b82f6' : '#ea580c',
                  },
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {/* NAVIGATION TABS - FULL WIDTH ON MOBILE (NO HORIZONTAL SCROLL) */}
      <Box sx={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant={isMobile ? 'fullWidth' : 'standard'}
          scrollButtons={false}
          sx={{
            minHeight: 44,
            '& .MuiTabs-flexContainer': {
              width: '100%',
            },
            '& .MuiTab-root': {
              minHeight: 44,
              minWidth: 0,
              flex: isMobile ? 1 : 'initial',
              py: 0.5,
              px: { xs: 0.5, sm: 1.5 },
              fontSize: { xs: '0.72rem', sm: '0.8rem' },
              fontWeight: 700,
              textTransform: 'none',
              '& .MuiTab-iconWrapper': {
                mr: { xs: 0.4, sm: 0.8 },
                mb: '0 !important',
              },
            },
          }}
        >
          <Tab
            icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={`Orders (${(detailData?.pricing_services || []).length})`}
          />
          <Tab
            icon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={`Invoices (${allInvoiceItems.length || invoicesHeaderList.length})`}
          />
          <Tab
            icon={<PaymentOutlinedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={`Payments (${paymentsList.length})`}
          />
          {paymentTermsList.length > 0 && (
            <Tab
              icon={<DateRangeOutlinedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={`Terms (${paymentTermsList.length})`}
            />
          )}
        </Tabs>
      </Box>

      {/* DIALOG CONTENT */}
      <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        {loading || !detailData ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={6} gap={1.5}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              Loading financial details...
            </Typography>
          </Box>
        ) : (
          <>
            {/* ============================================================== */}
            {/* TAB 0: ORDERS & SERVICES */}
            {/* ============================================================== */}
            {activeTab === 0 && (
              <Box>
                {(detailData.pricing_services || []).length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                    <Typography color="text.secondary">No order services configured</Typography>
                  </Paper>
                ) : (
                  (detailData.pricing_services || []).map((svc, idx) => {
                    const total = toNum(svc.order_total_amount);
                    const amount = toNum(svc.service_amount);
                    const count = toNum(svc.item_count || 1);
                    const gst = toNum(svc.service_gst_percentage);
                    const invoiced = toNum(svc.invoice_amount || svc.issue_invoice_amount);
                    const discount = toNum(svc.invoice_discount_amount);
                    const terms = svc.payment_terms || [];

                    return (
                      <Paper
                        key={`ord-${idx}`}
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          mb: 1.5,
                          borderRadius: 2.5,
                          border: '1px solid #e2e8f0',
                          backgroundColor: '#ffffff',
                        }}
                      >
                        {/* Service Title & Order Total */}
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} flexWrap="wrap" gap={1}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
                              {svc.service_name || 'Service'}
                            </Typography>
                            <Box display="flex" gap={1} alignItems="center" mt={0.2} flexWrap="wrap">
                              <Chip
                                size="small"
                                label={`Plan: ${svc.plan_name || 'Standard'}`}
                                sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#f1f5f9' }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Qty / Students: <strong>{count}</strong>
                              </Typography>
                              {svc.order_date && (
                                <Typography variant="caption" color="text.secondary">
                                  • Date: {svc.order_date}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          <Box textAlign={{ xs: 'left', sm: 'right' }}>
                            <Typography variant="caption" color="#64748b" display="block" fontSize="0.68rem" fontWeight={700}>
                              TOTAL ORDER
                            </Typography>
                            <Typography variant="h6" fontWeight={800} color="#0f172a">
                              {formatAmount(total)}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Breakdown Grid */}
                        <Box p={1.2} bgcolor="#f8fafc" borderRadius={2} border="1px solid #f1f5f9" mb={1}>
                          <Grid container spacing={1}>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="#64748b" fontSize="0.68rem" display="block">
                                Unit Rate
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="#334155">
                                {formatAmount(amount)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="#64748b" fontSize="0.68rem" display="block">
                                GST %
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="#334155">
                                {gst}%
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="#16a34a" fontSize="0.68rem" display="block">
                                Invoiced
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="#15803d">
                                {formatAmount(invoiced)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="#ea580c" fontSize="0.68rem" display="block">
                                Discount
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="#c2410c">
                                {formatAmount(discount)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Notes / Itemized Specs */}
                        {renderFormattedNotes(svc.notes)}

                        {/* Embedded Payment Terms for this service */}
                        {terms.length > 0 && (
                          <Box mt={1} pt={1} borderTop="1px dashed #e2e8f0">
                            <Typography variant="caption" fontWeight={700} color="#64748b" display="block" mb={0.5}>
                              Payment Milestones ({terms.length}):
                            </Typography>
                            <Grid container spacing={1}>
                              {terms.map((t, tIdx) => (
                                <Grid item xs={12} sm={6} key={`st-${tIdx}`}>
                                  <Box
                                    p={0.8}
                                    bgcolor="#ffffff"
                                    borderRadius={1.5}
                                    border="1px solid #e2e8f0"
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography variant="caption" fontWeight={700} color="#1e293b" display="block">
                                        {t.term_name || `Term ${tIdx + 1}`}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" fontSize="0.68rem">
                                        Due: {t.term_receive_date || '-'}
                                      </Typography>
                                    </Box>
                                    <Box textAlign="right">
                                      <Typography variant="body2" fontWeight={700} color="#0f172a">
                                        {formatAmount(t.amount_rs)}
                                      </Typography>
                                      {t.amount_percentage ? (
                                        <Typography variant="caption" color="#2563eb" fontWeight={600} fontSize="0.65rem">
                                          {t.amount_percentage}%
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}
                      </Paper>
                    );
                  })
                )}
              </Box>
            )}

            {/* ============================================================== */}
            {/* TAB 1: INVOICES (TAX & ISSUED) */}
            {/* ============================================================== */}
            {activeTab === 1 && (
              <Box>
                {/* Sub-filter toggle if multiple types exist */}
                {taxInvoiceItems.length > 0 && issueInvoiceItems.length > 0 && (
                  <Box mb={2} display="flex" justifyContent="flex-start">
                    <ToggleButtonGroup
                      value={invoiceTypeFilter}
                      exclusive
                      onChange={(e, val) => val && setInvoiceTypeFilter(val)}
                      size="small"
                      fullWidth={isMobile}
                      sx={{
                        width: isMobile ? '100%' : 'auto',
                        '& .MuiToggleButton-root': {
                          flex: isMobile ? 1 : 'initial',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: isMobile ? '0.68rem' : '0.75rem',
                          py: 0.4,
                          px: { xs: 0.8, sm: 1.5 },
                        },
                      }}
                    >
                      <ToggleButton value="all">All Invoices ({allInvoiceItems.length})</ToggleButton>
                      <ToggleButton value="tax">Tax Invoices ({taxInvoiceItems.length})</ToggleButton>
                      <ToggleButton value="issued">Issued Invoices ({issueInvoiceItems.length})</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}

                {/* FINAL INVOICES HEADER SUMMARY (IF PRESENT IN DB) */}
                {invoicesHeaderList.length > 0 && invoiceTypeFilter !== 'issued' && (
                  <Box mb={2}>
                    <Typography variant="caption" fontWeight={800} color="#64748b" textTransform="uppercase" display="block" mb={1}>
                      Header Invoices & Balances
                    </Typography>
                    {invoicesHeaderList.map((inv, idx) => {
                      const st = getStatusColor(inv.status);
                      return (
                        <Paper
                          key={`inv-h-${idx}`}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            bgcolor: '#ffffff',
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="body2" fontWeight={800} color="#0f172a">
                              Invoice #{inv.invoice_number || 'Draft'}
                            </Typography>
                            <Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.text, fontWeight: 700, fontSize: '0.68rem', height: 20 }} />
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                            <Typography variant="caption" color="text.secondary">
                              Total: <strong>{formatAmount(inv.total_amount)}</strong>
                            </Typography>
                            <Typography variant="caption" color="#16a34a" fontWeight={700}>
                              Paid: {formatAmount(inv.total_paid)}
                            </Typography>
                            <Typography variant="caption" color="#ea580c" fontWeight={700}>
                              Balance: {formatAmount(inv.balance)}
                            </Typography>
                            {inv.issue_invoice_number && (
                              <Typography variant="caption" color="text.secondary">
                                Issued Ref: #{inv.issue_invoice_number}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}

                {/* INVOICE LINE ITEMS */}
                <Typography variant="caption" fontWeight={800} color="#64748b" textTransform="uppercase" display="block" mb={1}>
                  Detailed Line Items ({filteredInvoices.length})
                </Typography>

                {filteredInvoices.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                    <Typography color="text.secondary">No invoices generated yet</Typography>
                  </Paper>
                ) : (
                  filteredInvoices.map((it, idx) => {
                    const st = getStatusColor(it.status);
                    const invNumber = it.invoice_number || it.invoice || it.issue_invoice || `Item ${idx + 1}`;
                    const discount = toNum(it.discount_amount);
                    const taxAmt = toNum(it.tax_amount);
                    const baseAmt = toNum(it.total_without_tax || it.amount);
                    const totalAmt = toNum(it.total);

                    return (
                      <Paper
                        key={`inv-item-${idx}`}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          mb: 1.2,
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          backgroundColor: '#ffffff',
                        }}
                      >
                        {/* Title Bar */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={800} color="#0f172a">
                              #{invNumber}
                            </Typography>
                            <Chip
                              size="small"
                              label={it.type}
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: it.type === 'Tax Invoice' ? '#eff6ff' : '#f8fafc',
                                color: it.type === 'Tax Invoice' ? '#2563eb' : '#64748b',
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                          <Chip
                            size="small"
                            label={st.label}
                            sx={{ bgcolor: st.bg, color: st.text, fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                          />
                        </Box>

                        {/* Meta info */}
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                          Service: <strong>{it.service_name}</strong>
                          {it.issue_date ? ` • Date: ${it.issue_date}` : ''}
                          {it.due_date ? ` • Due: ${it.due_date}` : ''}
                          {it.item_count ? ` • Qty: ${it.item_count}` : ''}
                        </Typography>

                        {/* Financial figures */}
                        <Box p={1} bgcolor="#f8fafc" borderRadius={2} border="1px solid #f1f5f9">
                          <Grid container spacing={1}>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="#64748b" fontSize="0.68rem" display="block">
                                Base (Excl. Tax)
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="#334155">
                                {formatAmount(baseAmt)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="#64748b" fontSize="0.68rem" display="block">
                                Tax {it.tax_percent ? `(${it.tax_percent}%)` : ''}
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="#334155">
                                {formatAmount(taxAmt)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="#ea580c" fontSize="0.68rem" display="block">
                                Discount
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="#c2410c">
                                {formatAmount(discount)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3} textAlign={{ xs: 'left', sm: 'right' }}>
                              <Typography variant="caption" color="#16a34a" fontSize="0.68rem" display="block" fontWeight={700}>
                                Total (Incl. Tax)
                              </Typography>
                              <Typography variant="body1" fontWeight={800} color="#0f172a">
                                {formatAmount(totalAmt)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Notes / Itemized Specs */}
                        {renderFormattedNotes(it.notes)}
                      </Paper>
                    );
                  })
                )}
              </Box>
            )}

            {/* ============================================================== */}
            {/* TAB 2: PAYMENTS */}
            {/* ============================================================== */}
            {activeTab === 2 && (
              <Box>
                {/* Payments summary stats */}
                <Box p={1.5} mb={2} bgcolor="#ffffff" borderRadius={2} border="1px solid #e2e8f0">
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="#64748b" display="block">Total Payments</Typography>
                      <Typography variant="body1" fontWeight={800} color="#0f172a">
                        {paymentsList.length} transactions
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="#16a34a" display="block">Total Collected</Typography>
                      <Typography variant="body1" fontWeight={800} color="#15803d">
                        {formatAmount(totalPaid)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="#64748b" display="block">Latest Payment Date</Typography>
                      <Typography variant="body2" fontWeight={600} color="#334155">
                        {detailData.payments_summary?.latest_payment_date || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {paymentsList.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                    <Typography color="text.secondary">No payment transactions recorded</Typography>
                  </Paper>
                ) : (
                  paymentsList.map((p, idx) => (
                    <Paper
                      key={`pay-${idx}`}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: 1.2,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                        <Box>
                          <Typography variant="body2" fontWeight={800} color="#0f172a">
                            Payment #{p.payment || idx + 1}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Date: <strong>{p.payment_date || '-'}</strong> • Mode:{' '}
                            <strong>{p.method || 'Online'}</strong>
                          </Typography>
                        </Box>
                        <Typography variant="subtitle1" fontWeight={800} color="#15803d">
                          {formatAmount(p.amount_paid)}
                        </Typography>
                      </Box>

                      {(p.reference || p.collected_by || p.invoice_number || p.notes) && (
                        <Box p={1} bgcolor="#f8fafc" borderRadius={1.5} border="1px solid #f1f5f9" mt={1}>
                          <Grid container spacing={1}>
                            {p.reference && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="#64748b" display="block">
                                  Reference / UTR:
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="#334155">
                                  {p.reference}
                                </Typography>
                              </Grid>
                            )}
                            {p.collected_by && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="#64748b" display="block">
                                  Collected By:
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="#334155">
                                  {p.collected_by}
                                </Typography>
                              </Grid>
                            )}
                            {p.invoice_number && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="#64748b" display="block">
                                  Against Invoice:
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="#2563eb">
                                  #{p.invoice_number}
                                </Typography>
                              </Grid>
                            )}
                            {p.notes && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" fontStyle="italic" display="block">
                                  Note: {p.notes}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  ))
                )}
              </Box>
            )}

            {/* ============================================================== */}
            {/* TAB 3: PAYMENT TERMS */}
            {/* ============================================================== */}
            {activeTab === 3 && paymentTermsList.length > 0 && (
              <Box>
                {paymentTermsList.map((term, idx) => (
                  <Paper
                    key={`term-${idx}`}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      mb: 1.2,
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                      bgcolor: '#ffffff',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="body2" fontWeight={800} color="#0f172a">
                        {term.term_name || `Term ${idx + 1}`} ({term.service_name})
                      </Typography>
                      <Typography variant="body1" fontWeight={800} color="#1e293b">
                        {formatAmount(term.amount_rs)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Due Date: <strong>{term.term_receive_date || '-'}</strong>
                      </Typography>
                      {term.amount_percentage ? (
                        <Typography variant="caption" color="#2563eb" fontWeight={700}>
                          {term.amount_percentage}% of order
                        </Typography>
                      ) : null}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* DIALOG ACTIONS WITH ROW NAVIGATION */}
      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 2 },
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {totalCount > 1 ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={!onPrev || currentIndex <= 0}
              onClick={onPrev}
              startIcon={<NavigateBeforeIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                fontSize: '0.78rem',
                borderColor: '#cbd5e1',
                color: '#334155',
              }}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!onNext || currentIndex >= totalCount - 1}
              onClick={onNext}
              endIcon={<NavigateNextIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                fontSize: '0.78rem',
                borderColor: '#cbd5e1',
                color: '#334155',
              }}
            >
              Next
            </Button>
            {currentIndex >= totalCount - 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                (Last Company)
              </Typography>
            )}
          </Box>
        ) : (
          <Box />
        )}
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
            bgcolor: '#0f172a',
            '&:hover': { bgcolor: '#1e293b' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CompanyBillingSummaryModal;
